import { ReactNode } from 'react'

import { fontWeightBold } from '@/constants'
import { useLayout } from '@/providers'
import {
	alpha,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	SxProps,
	Tooltip,
	useTheme,
} from '@mui/material'
import { getTestId } from '@utils/helpers'
import { Link } from 'react-router-dom'

type Props = {
	selected?: boolean
	icon: ReactNode
	primary: string
	to?: string
	hotkey?: string
	state?: {
		importComponentName?: string
	}
	sx?: SxProps
	onClick?(): void
}

export default function NavButton({
	icon,
	primary,
	to = null,
	selected = false,
	state = {},
	onClick = null,
	sx = {},
	...rest
}: Props) {
	const testId = getTestId(rest, 'nav-button')

	const theme = useTheme()
	const { sidebarExpanded } = useLayout()

	// Styling of nav button depends on theme
	const getNavButtonStyles = (theme) => {
		const isDark = theme.palette.mode === 'dark'

		return {
			height: '3rem',
			borderLeft: '0.5rem solid transparent',
			paddingX: '0.5rem',
			'&.Mui-selected': {
				backgroundColor: isDark ? alpha(theme.palette.secondary.main, 0.1) : 'secondary.lighter',
				color: 'secondary.dark',

				borderColor: isDark
					? alpha(theme.palette.secondary.main, 0.16)
					: theme.palette.secondary.dark,
				'& .MuiListItemIcon-root': { color: 'secondary.dark' },
				'& .MuiListItemText-primary': { fontWeight: fontWeightBold },
				'&:hover, :focus': {
					backgroundColor: isDark ? alpha(theme.palette.secondary.main, 0.1) : 'secondary.lighter',
				},
			},
			'&:not(.Mui-selected):hover, &:not(.Mui-selected):focus': {
				borderLeft: '0.5rem solid',
				color: 'secondary.main',
				backgroundColor: isDark ? alpha(theme.palette.secondary.main, 0.1) : 'secondary.lighter',
				borderColor: isDark
					? alpha(theme.palette.secondary.main, 0.08)
					: theme.palette.secondary.light,
				'& .MuiListItemIcon-root': { color: 'secondary.main' },
			},
		}
	}

	return (
		<Tooltip title={!sidebarExpanded ? primary : ''} placement="right">
			<ListItem disablePadding sx={{ ...sx }}>
				<ListItemButton
					selected={selected}
					component={to ? Link : 'button'}
					{...(onClick ? { onClick } : {})}
					{...(to ? { to } : {})}
					{...(to ? { state } : {})}
					sx={{ ...getNavButtonStyles(theme), ...sx }}
					data-testid={testId}
				>
					<ListItemIcon
						sx={{
							color: `${theme.palette.text.primary}`,
							minWidth: '1.5rem',
							display: 'flex',
							justifyContent: 'center',
						}}
					>
						{icon}
					</ListItemIcon>

					{sidebarExpanded && (
						<ListItemText
							sx={{
								mb: 0,
								pl: 1,
								whiteSpace: 'nowrap',
							}}
							primary={primary}
							slotProps={{ primary: { variant: 'body2' } }}
						/>
					)}
				</ListItemButton>
			</ListItem>
		</Tooltip>
	)
}
